import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Loader2, RefreshCw, AlertTriangle, ChevronRight, ChevronDown, Search,
  FileSpreadsheet, FileText,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * FirstSaleBeneficiaries — CHA-404. Imports → First Sale → Beneficiaries.
 *
 * Each LC beneficiary with the factories it has actually shipped through, resolved to company names
 * and addresses via the ERP location master (`lookup_locations`, Admin → Lookup Manager → Locations).
 *
 * The pairing is OBSERVED from live orders, never declared — so it cannot go stale, and a wrong pair
 * cannot be legitimized by hand-entering it. That is what makes a transposition legible: DONFEN
 * (the beneficiary) shows DONJIA (its factory) on 58 orders, so the 3 POs carrying them the other way
 * around read as an anomaly rather than a First Sale violation.
 *
 * IN SCOPE = CPLAN into a US warehouse. Only those orders can use First Sale at all, so only their
 * terms mean anything. Showing scope explicitly is the point: a beneficiary can carry WI and W3
 * orders and still be fully compliant, because those are DDP or non-US shipments.
 */

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);
const num = (v) => Number(v || 0).toLocaleString();

// A beneficiary's compliance reading, IN SCOPE ONLY. Out-of-scope orders never make a verdict.
export function verdictFor(row) {
  if (!row.in_scope_orders) return { key: 'none', label: 'nothing in scope', tone: 'bg-slate-100 text-slate-600' };
  if (row.approved) {
    return row.nonfs_orders
      ? { key: 'missing-fs', label: `${row.nonfs_orders} in-scope order(s) not on FS`, tone: 'bg-amber-100 text-amber-800' }
      : { key: 'ok', label: 'approved, all on FS', tone: 'bg-green-100 text-green-800' };
  }
  return row.fs_orders
    ? { key: 'wrong-vendor', label: `on FS but ${row.inProgram ? 'not approved' : 'not in the program'}`, tone: 'bg-red-100 text-red-800' }
    : { key: 'out', label: 'not in program, not claiming FS', tone: 'bg-slate-100 text-slate-600' };
}

// ─────────────────────────── CHA-430 — Excel / PDF export ───────────────────────────
// Pure, named-exported row builders (same convention as verdictFor above) so they can be unit-tested
// without a DOM or a spreadsheet library. Counts and money stay NUMERIC — a '$1,234' string lands in
// Excel as text and cannot be summed, which defeats the point of a spreadsheet. null stays null so an
// empty cell reads as "no data" rather than "zero dollars".
const numOrNull = (v) => (v == null ? null : Number(v));

export function beneficiaryExportRows(rows) {
  return (rows || []).map((r) => ({
    'LC beneficiary': r.code || '',
    Company: r.name || '',
    Address: r.address || '',
    'In program': r.inProgram ? 'Yes' : 'No',
    'Program vendor': r.vendor_name || '',
    Approved: r.approved ? 'Yes' : 'No',
    Factories: (r.factories || []).length,
    Orders: numOrNull(r.orders),
    'In scope': numOrNull(r.in_scope_orders),
    'On FS': numOrNull(r.fs_orders),
    'Not on FS': numOrNull(r.nonfs_orders),
    'In-scope FOB': numOrNull(r.in_scope_fob),
    // The on-screen badge is the whole point of the row; without it a reader has to re-derive the
    // compliance reading by hand from four count columns.
    Reading: verdictFor(r).label,
  }));
}

// The Factories sheet's column contract. Ruling 1 promises a STABLE two-sheet workbook, so the sheet
// must exist with its headers even when the filtered selection happens to contain no factories —
// otherwise a downstream join or Power Query refresh breaks on a missing sheet rather than returning
// zero rows, which is a much harder failure to read (Codex P2). A test pins this against the builder's
// own keys so the two cannot drift.
export const FACTORY_COLUMNS = [
  'LC beneficiary', 'Beneficiary company', 'Factory code', 'Factory company', 'Factory address',
  'Divisions', 'Terms', 'Orders', 'In scope', 'On FS', 'Not on FS', 'In-scope FOB',
];

// Operator ruling 1: Excel gets a second, joinable sheet rather than collapsing factories into a cell.
// Every factory row repeats its beneficiary code so the two sheets join on it.
export function factoryExportRows(rows) {
  const out = [];
  for (const r of rows || []) {
    for (const f of r.factories || []) {
      out.push({
        'LC beneficiary': r.code || '',
        'Beneficiary company': r.name || '',
        'Factory code': f.code || '',
        'Factory company': f.name || '',
        'Factory address': f.address || '',
        Divisions: (f.divisions || []).join(', '),
        Terms: (f.terms || []).join(', '),
        Orders: numOrNull(f.orders),
        'In scope': numOrNull(f.in_scope_orders),
        'On FS': numOrNull(f.fs_orders),
        'Not on FS': numOrNull(f.nonfs_orders),
        'In-scope FOB': numOrNull(f.in_scope_fob),
      });
    }
  }
  return out;
}

// Built here rather than inline in the handler so head/body arity is unit-testable — a head column
// without its body counterpart shifts every column after it, silently.
export function beneficiaryPdfTable(rows) {
  return {
    head: [['LC beneficiary', 'Company', 'Program vendor', 'Fact.', 'Orders', 'In scope', 'On FS', 'Not on FS', 'In-scope FOB', 'Reading']],
    body: (rows || []).map((r) => [
      r.code || '',
      r.name || '',
      r.vendor_name || '',
      String((r.factories || []).length),
      num(r.orders),
      num(r.in_scope_orders),
      num(r.fs_orders),
      num(r.nonfs_orders),
      money(r.in_scope_fob),
      verdictFor(r).label,
    ]),
  };
}

export function beneficiaryFilterNote({ inScopeOnly, q, shown, total }) {
  const bits = [];
  if (inScopeOnly) bits.push('in-scope orders only');
  if ((q || '').trim()) bits.push(`search "${q.trim()}"`);
  return bits.length
    ? `Filtered: ${bits.join(' + ')} — ${shown} of ${total} beneficiaries`
    : `All ${total} beneficiaries (no filter applied)`;
}

// Landscape A4 is 297mm wide; the table sits at a 14mm left margin, so this is the usable text width.
const PDF_TEXT_WIDTH = 269;

const stamp = () => new Date().toISOString().slice(0, 10);

export default function FirstSaleBeneficiaries() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [inScopeOnly, setInScopeOnly] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getBeneficiaryFactories', { timeout: 150000 });
      const res = await fn({});
      setData(res.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const term = q.trim().toUpperCase();
    return (data?.rows || [])
      .filter((r) => !inScopeOnly || r.in_scope_orders > 0)
      .filter((r) => !term
        || r.code.includes(term)
        || (r.name || '').toUpperCase().includes(term)
        || r.factories.some((f) => f.code.includes(term) || (f.name || '').toUpperCase().includes(term)));
  }, [data, q, inScopeOnly]);

  const note = () => beneficiaryFilterNote({
    inScopeOnly, q, shown: rows.length, total: (data?.rows || []).length,
  });

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(beneficiaryExportRows(rows)), 'Beneficiaries');
    // Ruling 1 — the factory detail travels as its own sheet, joinable on 'LC beneficiary'. Built from
    // the FILTERED rows, so the two sheets always describe the same population.
    const factories = factoryExportRows(rows);
    // ALWAYS appended, headers and all. A filter can legitimately leave only factory-less beneficiaries,
    // and a workbook whose second sheet vanishes breaks a saved join outright instead of returning no
    // rows (Codex P2).
    XLSX.utils.book_append_sheet(wb, factories.length
      ? XLSX.utils.json_to_sheet(factories)
      : XLSX.utils.aoa_to_sheet([FACTORY_COLUMNS]), 'Factories');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { Field: 'Report', Value: 'First Sale — LC beneficiaries and observed factories' },
      { Field: 'Generated', Value: new Date().toLocaleString() },
      { Field: 'In scope means', Value: 'CPLAN into a US warehouse — the only orders First Sale can apply to' },
      { Field: 'Pairing', Value: 'Observed from live orders, not a maintained list' },
      { Field: 'Filter', Value: note() },
      { Field: 'Beneficiaries in file', Value: rows.length },
      { Field: 'Factory rows', Value: factories.length },
    ]), 'Export info');
    XLSX.writeFile(wb, `first_sale_beneficiaries_${stamp()}.xlsx`);
  };

  const exportPdf = () => {
    // Ruling 1 — beneficiaries only. A factory-grain table runs several times longer and pages badly;
    // the joinable detail is what the Excel export is for.
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const { head, body } = beneficiaryPdfTable(rows);
    const caption = `${note()} · in scope = CPLAN into a US warehouse · generated ${new Date().toLocaleString()}`;
    // Wrapped, and the header block sized from the ACTUAL line count. A long search term ran the
    // caption off the right edge, clipping the timestamp — the provenance it exists to carry (Codex P2).
    doc.setFontSize(9);
    const capLines = doc.splitTextToSize(caption, PDF_TEXT_WIDTH);
    const top = 20 + capLines.length * 4;
    autoTable(doc, {
      startY: top,
      // startY applies to the FIRST page only; continuation pages begin at margin.top. Without this the
      // didDrawPage caption paints straight over the repeated header and first data row of every page
      // after the first — the page-stamp fix creating a worse bug than the one it solved (Codex P1).
      margin: { top },
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85] },
      head,
      body,
      columnStyles: {
        3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' },
        6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' },
        9: { cellWidth: 45 },
      },
      // Every page, not just the first — pages 2+ get separated and handed on, and a filtered extract
      // that does not say so reads as the whole book (self-review). This list pages at realistic volumes.
      didDrawPage: () => {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('First Sale — LC beneficiaries', 14, 14);
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(capLines, 14, 20);
      },
    });
    doc.save(`first_sale_beneficiaries_${stamp()}.pdf`);
  };

  const toggle = (code) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
        Every LC beneficiary with the factories it has actually shipped through — read from live open
        orders, not a maintained list, so it always matches reality. Company names and addresses come
        from the ERP location master (Admin → Lookup Manager → <span className="font-medium">Locations</span>).
        <span className="block mt-1">
          <span className="font-medium">In scope</span> means CPLAN into a US warehouse — the only orders First Sale can apply to.
          A DDP or non-US order is not an FOB purchase entering the US, so its terms are not a compliance signal.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search code or company…"
            className="pl-7 pr-2 py-1 text-sm border border-slate-300 rounded w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={inScopeOnly} onChange={(e) => setInScopeOnly(e.target.checked)} />
          Only beneficiaries with in-scope orders
        </label>
        {data && (
          <span className="text-xs text-slate-500">
            {rows.length} of {data.rows.length} beneficiaries · master holds {num(data.masterSize)} locations
            {data.unknownCodes > 0 && (
              <span className="ml-1 text-amber-700" title="These codes have no row in the location master, so they show as a bare code. Refresh the master from the BCADMIN export.">
                · {data.unknownCodes} code(s) the master can’t name
              </span>
            )}
          </span>
        )}
        {/* Disabled with no rows — an empty workbook reads as a real answer. Note the filters can
            legitimately empty this list (a search that matches nothing), so this is not just a
            still-loading guard. */}
        <button
          onClick={exportExcel} disabled={!rows.length}
          title={rows.length ? 'Download beneficiaries + factories as Excel (two sheets)' : 'Nothing to export yet'}
          className="ml-auto inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded"
        >
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </button>
        <button
          onClick={exportPdf} disabled={!rows.length}
          title={rows.length ? 'Download the beneficiary list as PDF' : 'Nothing to export yet'}
          className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded"
        >
          <FileText className="h-4 w-4" /> PDF
        </button>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}
      {loading && !data && <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading beneficiaries…</div>}

      {data && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="text-slate-500 text-xs bg-slate-50">
              <tr>
                {['', 'LC beneficiary', 'Company', 'Factories', 'Orders', 'In scope', 'On FS', 'Not on FS', 'In-scope FOB', 'Reading'].map((h, i) => (
                  <th key={i} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const v = verdictFor(r);
                const isOpen = open.has(r.code);
                return (
                  <React.Fragment key={r.code}>
                    <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(r.code)}>
                      <td className="px-2 py-1.5 text-slate-400">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs font-medium">{r.code}</td>
                      <td className="px-3 py-1.5">
                        {r.name || <span className="text-amber-700 text-xs">not in the location master</span>}
                        {r.inProgram && <span className="ml-1.5 text-[10px] text-slate-400">({r.vendor_name})</span>}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{r.factories.length}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{num(r.orders)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{num(r.in_scope_orders)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{num(r.fs_orders)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{num(r.nonfs_orders)}</td>
                      <td className="px-3 py-1.5 tabular-nums font-medium">{money(r.in_scope_fob)}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${v.tone}`}>{v.label}</span>
                      </td>
                    </tr>
                    {isOpen && r.factories.map((f) => (
                      <tr key={`${r.code}-${f.code}`} className="bg-slate-50/60 text-xs">
                        <td />
                        <td className="px-3 py-1.5 font-mono text-slate-500 pl-6">↳ {f.code}</td>
                        <td className="px-3 py-1.5 text-slate-600">
                          {f.name || <span className="text-amber-700">not in the location master</span>}
                          {f.address && <span className="block text-[11px] text-slate-400">{f.address}</span>}
                        </td>
                        <td className="px-3 py-1.5 text-slate-400">div {f.divisions.join(', ') || '—'}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-500">{num(f.orders)}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-500">{num(f.in_scope_orders)}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-500">{num(f.fs_orders)}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-500">{num(f.nonfs_orders)}</td>
                        <td className="px-3 py-1.5 tabular-nums text-slate-500">{money(f.in_scope_fob)}</td>
                        <td className="px-3 py-1.5 text-slate-400 font-mono">{f.terms.join(', ')}</td>
                      </tr>
                    ))}
                    {isOpen && r.address && (
                      <tr className="bg-slate-50/60 text-[11px] text-slate-400">
                        <td /><td /><td className="px-3 pb-2" colSpan={8}>{r.code} — {r.address}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <div className="px-3 py-6 text-sm text-slate-500">No beneficiaries match.</div>}
        </div>
      )}
    </div>
  );
}
